import React, { useState, useRef } from 'react';
import { User, Lock, Eye, EyeOff, Hotel, Wallet } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import Logo from '../components/Logo';
import ParticlesBackground from '../components/ParticlesBackground';

export default function SignInPage({ onLogin }) {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [credentials, setCredentials] = useState({ login: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleSubmit = async () => {
    setError('');

    if (!captchaToken) {
      setError('Veuillez confirmer que vous n\'êtes pas un robot.');
      return;
    }

    setIsLoading(true);
    try {
      await onLogin({ ...credentials, captchaToken });
    } catch (err) {
      setError(err.message || 'Identifiants invalides');
      // Reset captcha on failed login so they must re-check
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2B4979] via-[#3d5c8f] to-[#2B4979] flex items-center justify-center p-4 relative overflow-hidden">
      <ParticlesBackground />

      <div className="w-full max-w-6xl grid md:grid-cols-2 relative z-10 shadow-2xl rounded-2xl overflow-hidden">
        {/* Left Side - Login */}
        <div className="bg-white p-8 md:p-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#2B4979] mb-2">Connexion</h1>
            <p className="text-gray-600">Accédez à votre espace partenaire</p>
          </div>

          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="flex items-center gap-2 text-[#2B4979] font-semibold mb-2">
                <User size={18} />
                Identifiant
              </label>
              <input
                type="text"
                placeholder="Votre identifiant"
                value={credentials.login}
                onChange={(e) => setCredentials({ ...credentials, login: e.target.value })}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF7B47] focus:outline-none transition"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-[#2B4979] font-semibold mb-2">
                <Lock size={18} />
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Votre mot de passe"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#FF7B47] focus:outline-none transition pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#2B4979]"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-[#FF7B47] border-gray-300 rounded focus:ring-[#FF7B47]"
                />
                <span className="text-sm text-gray-600">Se souvenir de moi</span>
              </label>
              <button type="button" className="text-sm text-[#FF7B47] hover:underline">
                Mot de passe oublié ?
              </button>
            </div>

            {/* reCAPTCHA widget */}
            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LdXE3wsAAAAAL7BQVm41u8oPyq4rEfryQANtDx7"
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || !captchaToken}
              className="w-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white font-semibold py-3 rounded-lg transition duration-200 transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? 'CONNEXION...' : 'SE CONNECTER'}
            </button>
          </div>

<div className="mt-6 text-center text-sm text-gray-500">
  © {new Date().getFullYear()} GOMAX Travel & Tourism
</div>
        </div>

        {/* Right Side - Welcome */}
        <div className="bg-gradient-to-br from-[#2B4979] via-[#3d5c8f] to-[#2B4979] text-white p-8 md:p-12">
          <div className="mb-8">
            <Logo />
          </div>

          <h2 className="text-4xl font-bold mb-4">Bienvenue !</h2>
          <p className="text-lg text-white/90 mb-8">
            Connectez-vous à votre espace B2B et profitez de tarifs exclusifs.
          </p>

          <div className="space-y-4">
            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
              <div className="bg-[#FF7B47] p-3 rounded-lg">
                <Hotel size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Réservations Instantanées</h3>
                <p className="text-sm text-white/80">Accès en temps réel</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
              <div className="bg-[#FF7B47] p-3 rounded-lg">
                <Wallet size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Tarifs B2B Exclusifs</h3>
                <p className="text-sm text-white/80">Marges garanties</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
              <div className="bg-[#FF7B47] p-3 rounded-lg">
                <User size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Support 24/7</h3>
                <p className="text-sm text-white/80">Assistance dédiée</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}